"use client";

import { useEffect, useRef } from "react";

// Video cards' previews (VideoCard): with a mouse, the card pointed at plays; on phones and tablets, which
// can't point, the card nearest the middle of the screen plays while the visitor scrolls, one at a time.
// One IntersectionObserver for every card on the page finds those in the middle band of the screen; of
// those, the nearest the middle starts once it has stayed nearest for a moment, so a fling past a dozen
// cards starts none of them, and stops as soon as it leaves the band. None plays before the visitor first
// scrolls or touches the page: a preview that starts by itself on arrival would be the page's last big picture
// to load (its Largest Contentful Paint) and, under the 18+ notice, plays for nobody.

type Connection = { saveData?: boolean; effectiveType?: string };

/** The visitor asked to save data, or the connection is too slow for clips: cards show no previews. */
export function quietData() {
  const c = (navigator as Navigator & { connection?: Connection }).connection;
  return !!c && (c.saveData === true || c.effectiveType === "slow-2g" || c.effectiveType === "2g");
}

/** Previews follow the scroll here: the device's main input can't hover, and motion and data are welcome. */
const scrollPreviews = () =>
  window.matchMedia("(hover: none)").matches && !window.matchMedia("(prefers-reduced-motion: reduce)").matches && !quietData();

/** Waits this long with one card nearest the middle before it plays. */
const SETTLE_MS = 250;
/** The band, as a share of the screen's height cut off above and below it. */
const BAND = "-30% 0px -30% 0px";

const cards = new Map<Element, (on: boolean) => void>();
const inBand = new Set<Element>();
let observer: IntersectionObserver | null = null;
let playing: Element | null = null;
let next: Element | null = null;
let timer: number | undefined;
let frame = 0;
/** The visitor has scrolled, touched or typed since the page opened. */
let engaged = false;
const engage = () => {
  if (engaged) return;
  engaged = true;
  for (const type of ["scroll", "touchstart", "pointerdown", "keydown"]) window.removeEventListener(type, engage);
  schedule();
};

function setPlaying(el: Element | null) {
  if (playing === el) return;
  if (playing) cards.get(playing)?.(false);
  playing = el;
  if (el) cards.get(el)?.(true);
}

function pick() {
  frame = 0;
  if (!engaged) return;
  const x = window.innerWidth / 2;
  const y = window.innerHeight / 2;
  let best: Element | null = null;
  let bestDistance = Infinity;
  for (const el of inBand) {
    const r = el.getBoundingClientRect();
    const distance = Math.hypot(r.left + r.width / 2 - x, r.top + r.height / 2 - y);
    if (distance < bestDistance) [best, bestDistance] = [el, distance];
  }
  // The playing card stops as soon as it leaves the band; another starts once it's settled in the middle.
  if (playing && !inBand.has(playing)) setPlaying(null);
  if (best === next) return;
  next = best;
  window.clearTimeout(timer);
  if (best === playing) return;
  timer = window.setTimeout(() => setPlaying(next), SETTLE_MS);
}

const schedule = () => {
  if (!frame) frame = window.requestAnimationFrame(pick);
};

function watch(el: Element, onChange: (on: boolean) => void) {
  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) inBand.add(e.target);
          else inBand.delete(e.target);
        }
        schedule();
      },
      { rootMargin: BAND },
    );
    window.addEventListener("scroll", schedule, { passive: true });
    if (!engaged) for (const type of ["scroll", "touchstart", "pointerdown", "keydown"]) window.addEventListener(type, engage, { passive: true });
  }
  cards.set(el, onChange);
  observer.observe(el);
  return () => {
    observer?.unobserve(el);
    cards.delete(el);
    inBand.delete(el);
    if (playing === el) playing = null;
    if (next === el) next = null;
    schedule();
    if (cards.size === 0) {
      observer?.disconnect();
      observer = null;
      window.removeEventListener("scroll", schedule);
      window.clearTimeout(timer);
    }
  };
}

/**
 * On a device that can't hover, calls `onChange(true)` while the element is the card playing in the middle
 * of the screen and `onChange(false)` when it stops. Off when `enabled` is false (a card with nothing to play).
 */
export function useScrollPreview(ref: React.RefObject<Element | null>, enabled: boolean, onChange: (on: boolean) => void) {
  const latest = useRef(onChange);
  useEffect(() => {
    latest.current = onChange;
  });
  useEffect(() => {
    const el = ref.current;
    if (!enabled || !el || !scrollPreviews()) return;
    const stop = watch(el, (on) => latest.current(on));
    return () => {
      stop();
      latest.current(false);
    };
  }, [ref, enabled]);
}
